/**
 * platform-admins-page.tsx — Quản lý tài khoản super-admin. Route `/platform/admins`.
 * Thêm người vận hành thứ 2+, khoá khi nhân sự nghỉ, buộc đăng xuất mọi phiên.
 */
import { useState } from 'react'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { Plus, LogOut, Loader2 } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { DataTable, type Column } from '@/components/shared/data-table'
import { ErrorState } from '@/components/shared/feedback'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { platformApiError } from '@/lib/platform-client'
import {
  usePlatformAdmins, useCreatePlatformAdmin, useSetPlatformAdminActive,
  useLogoutAllPlatformAdmin, type PlatformAdminItem,
} from '@/hooks/use-platform'

export function PlatformAdminsPage() {
  const { data, isLoading, isError } = usePlatformAdmins()
  const create = useCreatePlatformAdmin()
  const setActive = useSetPlatformAdminActive()
  const logoutAll = useLogoutAllPlatformAdmin()

  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [password, setPassword] = useState('')

  const submit = () => {
    create.mutate(
      { email: email.trim(), fullName: fullName.trim(), password },
      {
        onSuccess: () => {
          toast.success('Đã thêm super-admin')
          setOpen(false); setEmail(''); setFullName(''); setPassword('')
        },
        onError: (e) => toast.error(platformApiError(e)),
      },
    )
  }

  const toggle = (a: PlatformAdminItem) => {
    setActive.mutate(
      { id: a.id, isActive: !a.isActive },
      {
        onSuccess: () => toast.success(a.isActive ? 'Đã khoá tài khoản' : 'Đã mở lại tài khoản'),
        onError: (e) => toast.error(platformApiError(e)),
      },
    )
  }

  const columns: Column<PlatformAdminItem>[] = [
    {
      key: 'name', header: 'Super-admin',
      cell: (a) => (
        <div className="min-w-0">
          <p className="truncate font-medium">
            {a.fullName} {a.isSelf && <span className="text-xs text-muted-foreground">(bạn)</span>}
          </p>
          <p className="truncate text-xs text-muted-foreground">{a.email}</p>
        </div>
      ),
    },
    {
      key: 'status', header: 'Trạng thái',
      cell: (a) => a.isActive ? <Badge variant="success">Đang hoạt động</Badge> : <Badge variant="secondary">Đã khoá</Badge>,
    },
    {
      key: 'lastLogin', header: 'Đăng nhập lần cuối',
      cell: (a) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {a.lastLoginAt ? dayjs(a.lastLoginAt).format('DD/MM/YYYY HH:mm') : 'Chưa từng'}
        </span>
      ),
    },
    {
      key: 'created', header: 'Tạo lúc',
      cell: (a) => <span className="whitespace-nowrap text-xs text-muted-foreground">{dayjs(a.createdAt).format('DD/MM/YYYY')}</span>,
    },
    {
      key: 'actions', header: '', align: 'right',
      cell: (a) => (
        <div className="flex justify-end gap-1.5">
          <Button
            variant="outline" size="sm"
            disabled={logoutAll.isPending}
            onClick={() => logoutAll.mutate(a.id, {
              onSuccess: () => toast.success('Đã buộc đăng xuất mọi phiên'),
              onError: (e) => toast.error(platformApiError(e)),
            })}
            title="Thu hồi mọi phiên đăng nhập"
          >
            <LogOut className="h-4 w-4" />
          </Button>
          <Button
            variant={a.isActive ? 'destructive' : 'default'} size="sm"
            disabled={setActive.isPending || (a.isSelf && a.isActive)}
            title={a.isSelf && a.isActive ? 'Không thể tự khoá tài khoản của mình' : ''}
            onClick={() => toggle(a)}
          >
            {a.isActive ? 'Khoá' : 'Mở lại'}
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title="Super-admin"
        description="Tài khoản quản trị toàn hệ thống. Phải luôn còn ít nhất một tài khoản đang hoạt động."
      />

      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {data ? `${data.activeCount}/${data.admins.length} đang hoạt động` : ''}
        </span>
        <Button onClick={() => setOpen(true)}><Plus /> Thêm super-admin</Button>
      </div>

      {isError ? (
        <ErrorState />
      ) : (
        <DataTable
          columns={columns}
          rows={data?.admins ?? []}
          rowKey={(a) => a.id}
          loading={isLoading}
          emptyTitle="Chưa có super-admin nào"
        />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Thêm super-admin</DialogTitle>
            <DialogDescription>
              Người này sẽ toàn quyền trên MỌI tổ chức trong hệ thống. Chỉ cấp cho người vận hành tin cậy.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Họ tên</Label>
              <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Nguyễn Văn A" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@congty.vn" />
            </div>
            <div className="space-y-1.5">
              <Label>Mật khẩu</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Tối thiểu 10 ký tự" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Huỷ</Button>
            <Button onClick={submit} disabled={create.isPending}>
              {create.isPending && <Loader2 className="animate-spin" />}
              Tạo tài khoản
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
